'use client';

import { RollingCounter } from '@/components/girl/digit';
import { useLikes } from '@/components/likes/like.provider';
import { cn } from '@bells/core/lib/utils';
import {
  attendantBlocked,
  attendantDateRequest,
  attendantMediaItemPrivateOnly,
  attendantNoInfoYet,
  attendantRatingPunctuality,
  attendantRatingResponseTime,
  attendantsAge,
  homeNewcomer,
} from '@bells/i18n';
import {
  AttendantProfileState,
  AttendantSearchSingleResponse,
} from '@bells/schema/models/attendant';
import { AspectRatio } from '@bells/ui/components/aspect-ratio';
import { Badge } from '@bells/ui/components/badge';
import { Button } from '@bells/ui/components/button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@bells/ui/components/carousel';
import { Separator } from '@bells/ui/components/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bells/ui/components/tooltip';
import dynamic from 'next/dynamic';
const VideoPlayer = dynamic(() => import('@bells/ui/components/video-player'), {
  loading: () => <Skeleton className='h-full w-full'></Skeleton>,
  ssr: false,
});

import { useMediaQuery } from '@bells/ui/hooks/use-media-query';
import { addMonths, isAfter } from 'date-fns';
import {
  Calendar,
  Gem,
  Gift,
  Heart,
  Loader2,
  Ruler,
  Send,
  UserRoundPlus,
  Volume2,
  VolumeX,
  Zap,
} from 'lucide-react';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@bells/ui/components/skeleton';
import { FlagIcon } from '@bells/ui/components/flag-icon';
import languages from '@bells/iso/languages';
import { useLanguage } from '@bells/core/i18n';

export const GirlDesktopMobile = ({
  data,
  setOpen,
}: {
  data: AttendantSearchSingleResponse;
  setOpen: (val: boolean) => void;
}) => {
  const isNew = useMemo(() => {
    if (!data.created_at) {
      return false;
    }
    const oneMonthAgo = addMonths(new Date(), -1);
    return isAfter(new Date(data.created_at), oneMonthAgo);
  }, [data.created_at]);

  const [api, setApi] = useState<CarouselApi>();
  const [muted, setMuted] = useState<boolean>(true);
  const [current, setCurrent] = useState(0);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { likes, doLike, isLoading, hasLike } = useLikes();
  const [showMuteOverlay, setShowMuteOverlay] = useState(true);
  const muteOverlayTimeout = useRef<NodeJS.Timeout | null>(null);
  const { language } = useLanguage();
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    api.on('select', onSelect);

    // set once on mount
    onSelect();

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);
  return (
    <div className='flex flex-col gap-2'>
      <div className='-ml-4 -mt-4 w-[100vw] pb-4'>
        <AspectRatio
          ratio={2 / 3}
          className='relative overflow-hidden bg-muted'
        >
          <Carousel
            setApi={setApi}
            opts={{ loop: true }}
            className='h-full w-full'
          >
            <CarouselContent>
              {(data.media ?? []).map((image, index) => (
                <CarouselItem key={index}>
                  <AspectRatio ratio={2 / 3} className='relative h-full w-full'>
                    {image?.type == 'image' && image.isVisible && (
                      <Image
                        placeholder={image.blurThumbnail ? 'blur' : undefined}
                        blurDataURL={image.blurThumbnail ?? undefined}
                        alt={`Image ${(index ?? 0) + 1}`}
                        width={0}
                        height={0}
                        unoptimized
                        fill
                        loading='lazy'
                        className='object-cover'
                        src={image.xl}
                      ></Image>
                    )}

                    {image?.type == 'video' &&
                      image.path &&
                      image.isVisible && (
                        <div className='relative w-full'>
                          <VideoPlayer
                            loop={true}
                            muted={muted}
                            paused={current != index + 1 || isDesktop}
                            autoPlay={true}
                            showControls={false}
                            poster={image.thumbnail}
                            aspectRatio={'2/3'}
                            src={image.path}
                          ></VideoPlayer>
                          <div
                            className='absolute left-0 top-0 z-[50] flex h-full w-full items-center justify-center'
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setMuted((prev) => !prev);
                              setShowMuteOverlay(true);

                              if (muteOverlayTimeout.current) {
                                clearTimeout(muteOverlayTimeout.current);
                              }

                              muteOverlayTimeout.current = setTimeout(() => {
                                setShowMuteOverlay(false);
                              }, 1000);
                            }}
                          >
                            <div
                              className={cn(
                                'animate-fade rounded-full bg-black/70 p-3 text-white opacity-100 transition-opacity delay-0 duration-500',
                                showMuteOverlay || muted
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              )}
                            >
                              {muted ? (
                                <VolumeX className='h-6 w-6' />
                              ) : (
                                <Volume2 className='h-6 w-6' />
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                    {image?.type == 'image' && !image.isVisible && (
                      <Image
                        alt={`Image ${(index ?? 0) + 1}`}
                        width={0}
                        height={0}
                        unoptimized
                        fill
                        loading='lazy'
                        className='object-cover'
                        placeholder={image.blurThumbnail ? 'blur' : undefined}
                        src={image.xl}
                        blurDataURL={image.blurThumbnail ?? undefined}
                      ></Image>
                    )}

                    {image?.type == 'image' && !image.isVisible && (
                      <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                        <Gem></Gem>
                        {attendantMediaItemPrivateOnly()}
                      </div>
                    )}

                    {image?.type == 'video' && !image.isVisible && (
                      <>
                        <Image
                          fill
                          unoptimized
                          loading='lazy'
                          src={image.thumbnail}
                          className='h-full w-full object-cover'
                          alt=''
                        ></Image>
                        <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                          <Gem></Gem>
                          {attendantMediaItemPrivateOnly()}
                        </div>
                      </>
                    )}
                  </AspectRatio>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className='dark pointer-events-none absolute bottom-0 left-0 w-full select-none text-foreground'>
            <div className='relative z-[2] bg-gradient-to-b from-transparent to-background/100 px-4 py-6'>
              <div className='flex items-end justify-between gap-2'>
                <div className='flex flex-1 flex-col gap-2'>
                  <div className='flex scroll-m-20 items-center gap-2 font-source text-3xl font-medium tracking-tight first:mt-0'>
                    <div className='flex flex-wrap gap-2'>
                      {isNew && (
                        <span className='break-keep bg-brand px-2 py-0 text-sm uppercase text-brand-foreground'>
                          {homeNewcomer()}
                        </span>
                      )}
                    </div>
                    {data.name}
                  </div>
                  <div className='flex flex-wrap gap-2 text-base'>
                    {data.locations?.map((df, index) => {
                      return (
                        <div
                          className='inline-flex items-center gap-1'
                          key={`loc-${index}`}
                        >
                          <FlagIcon
                            alt={
                              languages[df.countryCode.toUpperCase()]?.names[
                                language
                              ]
                            }
                            countryCode={df.countryCode.toUpperCase()}
                            className='mr-1 inline-block h-4 w-4'
                          ></FlagIcon>

                          {df.name}
                        </div>
                      );
                    })}

                    {data.height && (
                      <span className='whitespace-nowrap break-keep'>
                        <Ruler className='mr-1 inline-block h-4 w-4'></Ruler>
                        {`${data.height} cm`}
                      </span>
                    )}

                    {data.age && (
                      <span className='whitespace-nowrap break-keep'>
                        <Calendar className='mr-1 inline-block h-4 w-4'></Calendar>
                        {attendantsAge({ age: data.age })}
                      </span>
                    )}
                  </div>
                </div>

                <div className='relative w-[40px]'>
                  <div className='absolute bottom-0 right-0 flex w-[40px] flex-col gap-2'>
                    <Button
                      onClick={doLike}
                      disabled={isLoading}
                      className='group pointer-events-auto h-[60px] w-auto flex-col items-center p-2 hover:bg-transparent'
                      variant={'ghost'}
                      size={'xl'}
                    >
                      {isLoading ? (
                        <Loader2 className={cn('animate-spin')} />
                      ) : (
                        <Heart
                          className={cn(
                            'h-4 w-4',
                            hasLike
                              ? 'fill-pink-500 text-pink-500'
                              : 'text-pink-500'
                          )}
                        ></Heart>
                      )}
                      <RollingCounter
                        value={likes}
                        digitSize={18}
                      ></RollingCounter>
                    </Button>
                    <Button
                      disabled
                      size={'xl'}
                      className='h-[60px] w-auto flex-col items-center p-2'
                      variant={'ghost'}
                    >
                      <UserRoundPlus fill='rgb(0 0 0 / var(--tw-text-opacity, 1))'></UserRoundPlus>

                      <RollingCounter value={0} digitSize={18}></RollingCounter>
                    </Button>
                    <Button
                      disabled
                      className='h-[40px] w-auto flex-col items-center p-2'
                      variant={'ghost'}
                      size={'xl'}
                    >
                      <Send></Send>
                    </Button>

                    <Button
                      disabled
                      className='h-[40px] w-auto flex-col items-center p-2'
                      variant={'ghost'}
                      size={'xl'}
                    >
                      <Gift></Gift>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AspectRatio>
      </div>

      <Tooltip>
        <TooltipTrigger asChild>
          <div className='w-full'>
            <Button
              onClick={() => setOpen(true)}
              disabled={data.state !== AttendantProfileState.APPROVED}
              size={'lg'}
              className='w-full'
            >
              <Calendar></Calendar>
              {attendantDateRequest()}
            </Button>
          </div>
        </TooltipTrigger>
        {data.state !== AttendantProfileState.APPROVED && (
          <TooltipContent>{attendantBlocked()}</TooltipContent>
        )}
      </Tooltip>
      <Separator className='mb-4 mt-4 w-full'></Separator>

      <div className='grid grid-cols-2 gap-4'>
        <div className='flex flex-col gap-1'>
          <span className='text-base font-medium'>
            {attendantRatingPunctuality()}
          </span>
          <span>
            {/*<Rating disabled variant='yellow' rating={0}></Rating>*/}
            {attendantNoInfoYet()}
          </span>
        </div>

        <div className='flex flex-col gap-1'>
          <span className='text-base font-medium'>
            {attendantRatingResponseTime()}
          </span>
          <div>
            <Badge className='gap-1'>
              <Zap className='h-4 w-4'></Zap>
              {attendantNoInfoYet()}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};
